import { createI18n } from 'vue-i18n'
import { isRef, nextTick } from 'vue'
import type { Composer, I18n, I18nMode, I18nOptions, Locale, VueI18n } from 'vue-i18n'

export const DEFAULTLANG = 'en-US'
export const SUPPORT_LOCALES = getSupportLocales()

function getSupportLocales(): string[] {
  const locales = import.meta.glob(`./locales/*.json`)
  const supportLocales = Object.keys(locales)
  const keys = supportLocales.map((locale) => {
    const file = locale.split('/').pop()
    const key = file!.split('.')[0]
    return key
  })
  return keys
}
export function getLocale(i18n: I18n): string {
  const locale = isComposer(i18n.global, i18n.mode) ? i18n.global.locale.value : i18n.global.locale
  return locale === 'en' ? DEFAULTLANG : locale
}

function isComposer(instance: VueI18n | Composer, mode: I18nMode): instance is Composer {
  return mode === 'composition' && isRef(instance.locale)
}

export function setLocale(i18n: I18n, locale: Locale): void {
  if (isComposer(i18n.global, i18n.mode)) {
    i18n.global.locale.value = locale
  } else {
    i18n.global.locale = locale
  }
}
export function setupI18n(options: I18nOptions = { locale: DEFAULTLANG }): I18n {
  const i18n = createI18n(options)
  setI18nLanguage(i18n, options.locale!)
  return i18n
}

export function setI18nLanguage(i18n: I18n, locale: Locale): void {
  setLocale(i18n, locale)
  /**
   * NOTE:
   * If you need to specify the language setting for headers, such as the `fetch` API, set it here.
   * The following is an example for axios.
   *
   * axios.defaults.headers.common['Accept-Language'] = locale
   */
  document.querySelector('html')!.setAttribute('lang', locale)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getResourceMessages = (r: any) => r.default || r

export async function loadLocaleMessages(i18n: I18n, locale: Locale) {
  try {
    // console.log('loadLocaleMessages', locale)
    const messages = await import(`./locales/${locale}.json`).then(getResourceMessages).catch(() => {
      // console.log(`[vite-plugin-vue-i18n] Not found '${locale}' lang file`)
      return {}
    })
    if (Object.keys(messages).length === 0) {
      // console.warn(`[vite-plugin-vue-i18n] '${locale}' lang file is empty`)
      const fallbackLocale = i18n.global.fallbackLocale
      const fallback = locale.split('-')[0]
      console.log(`[Localisation] fall back to '${fallback}' lang file`)
      locale.includes('-') && loadLocaleMessages(i18n, fallback) // fallback to base locale
    }
    if (i18n.global.availableLocales.includes(locale)) {
      i18n.global.mergeLocaleMessage(locale, messages)
    } else {
      i18n.global.setLocaleMessage(locale, messages)
    }
    return nextTick()
  } catch (e) {
    console.error(e)
    return nextTick()
  }
}

export const i18n = setupI18n({
  legacy: false,
  locale: 'en',
  fallbackLocale: 'en',
  fallbackWarn: false,
  silentFallbackWarn: true,
  missingWarn: false,
  messages: {}
})
